import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { AdminLayout } from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import { 
  Plus, Users, Shield, GraduationCap, ClipboardCheck, UserCog,
  Lock, Unlock, Trash2, Pencil
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { id: "participant", label: "Teilnehmer", icon: Users, color: "bg-blue-500/10 text-blue-500" },
  { id: "dozent", label: "Dozent", icon: GraduationCap, color: "bg-green-500/10 text-green-500" },
  { id: "admin", label: "Administrator", icon: UserCog, color: "bg-yellow-500/10 text-yellow-500" },
  { id: "super_admin", label: "Super-Admin", icon: Shield, color: "bg-red-500/10 text-red-500" },
  { id: "auditor", label: "ISO-Auditor", icon: ClipboardCheck, color: "bg-purple-500/10 text-purple-500" }
];

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState({
    participant: [],
    dozent: [],
    admin: [],
    super_admin: [],
    auditor: []
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("participant");
  
  // Dialogs
  const [createDialog, setCreateDialog] = useState(false);
  const [roleDialog, setRoleDialog] = useState(false);
  
  // Form states
  const [createForm, setCreateForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    role: "dozent"
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState("");

  const isSuperAdmin = currentUser?.role === "super_admin";
  const isDozent = currentUser?.role === "dozent";
  const canSeeEmail = !isDozent;

  useEffect(() => {
    loadAllUsers();
  }, []);

  const loadAllUsers = async () => {
    try {
      const roles = ["participant", "dozent", "admin", "auditor"];
      if (isSuperAdmin) roles.push("super_admin");
      
      const results = await Promise.all(
        roles.map(role => axios.get(`${API}/users/by-role/${role}`).then(r => ({ role, users: r.data })))
      );
      
      const usersByRole = { participant: [], dozent: [], admin: [], super_admin: [], auditor: [] };
      results.forEach(r => {
        usersByRole[r.role] = r.users;
      });
      setUsers(usersByRole);
    } catch (error) {
      toast.error("Fehler beim Laden der Benutzer");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!createForm.name || !createForm.email || !createForm.password) {
      toast.error("Bitte alle Pflichtfelder ausfüllen");
      return;
    }
    
    try {
      await axios.post(`${API}/users/create-staff`, null, {
        params: {
          name: createForm.name,
          email: createForm.email,
          password: createForm.password,
          phone: createForm.phone,
          role: createForm.role
        }
      });
      toast.success("Benutzer erstellt");
      setCreateDialog(false);
      setCreateForm({ name: "", email: "", password: "", phone: "", role: "dozent" });
      loadAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen");
    }
  };

  const handleChangeRole = async () => {
    if (!selectedUser || !newRole) return;
    
    try {
      await axios.put(`${API}/users/${selectedUser.id}/role`, null, {
        params: { new_role: newRole }
      });
      toast.success("Rolle geändert");
      setRoleDialog(false);
      setSelectedUser(null);
      setNewRole("");
      loadAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Ändern");
    }
  };

  const handleLockUser = async (userId, lock) => {
    try {
      await axios.put(`${API}/users/${userId}`, { is_locked: lock });
      toast.success(lock ? "Benutzer gesperrt" : "Benutzer entsperrt");
      loadAllUsers();
    } catch (error) {
      toast.error("Fehler");
    }
  };

  const openRoleDialog = (user) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setRoleDialog(true);
  };

  const getRoleInfo = (roleId) => {
    return ROLES.find(r => r.id === roleId) || ROLES[0];
  };

  const currentTabUsers = users[activeTab] || [];
  const currentRoleInfo = getRoleInfo(activeTab);

  return (
    <AdminLayout title="Benutzerverwaltung">
      <div className="space-y-6" data-testid="user-management">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {ROLES.filter(r => r.id !== "super_admin" || isSuperAdmin).map(role => {
            const Icon = role.icon;
            const count = users[role.id]?.length || 0;
            return (
              <Card 
                key={role.id} 
                className={`bg-card border-border cursor-pointer transition-colors ${activeTab === role.id ? "ring-2 ring-primary" : ""}`}
                onClick={() => setActiveTab(role.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${role.color} flex items-center justify-center`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{count}</p>
                      <p className="text-xs text-muted-foreground">{role.label}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* User Table */}
        <Card className="bg-card border-border">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  {(() => { const Icon = currentRoleInfo.icon; return <Icon className="h-5 w-5" />; })()}
                  {currentRoleInfo.label} ({currentTabUsers.length})
                </CardTitle>
                <CardDescription>
                  {activeTab === "participant" && "Alle registrierten Teilnehmer"}
                  {activeTab === "dozent" && "Dozenten können Kurse verwalten"}
                  {activeTab === "admin" && "Administratoren haben volle Verwaltungsrechte"}
                  {activeTab === "super_admin" && "Super-Admins können alle Benutzer und Rollen verwalten"}
                  {activeTab === "auditor" && "ISO-Auditoren haben nur Leserechte im QM-Bereich"}
                </CardDescription>
              </div>
              {isSuperAdmin && activeTab !== "participant" && activeTab !== "super_admin" && (
                <Button onClick={() => { setCreateForm({ ...createForm, role: activeTab }); setCreateDialog(true); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  {currentRoleInfo.label} erstellen
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : currentTabUsers.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">
                Keine {currentRoleInfo.label} vorhanden
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    {canSeeEmail && <TableHead>E-Mail</TableHead>}
                    {isSuperAdmin && <TableHead>Telefon</TableHead>}
                    <TableHead>Status</TableHead>
                    <TableHead>Erstellt</TableHead>
                    {isSuperAdmin && <TableHead className="text-right">Aktionen</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentTabUsers.map(user => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      {canSeeEmail && <TableCell>{user.email}</TableCell>}
                      {isSuperAdmin && <TableCell className="text-muted-foreground">{user.phone || "-"}</TableCell>}
                      <TableCell>
                        {user.is_locked ? (
                          <Badge variant="destructive">Gesperrt</Badge>
                        ) : (
                          <Badge className="bg-green-500/10 text-green-500 border border-green-500/30">Aktiv</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {new Date(user.created_at).toLocaleDateString("de-DE")}
                      </TableCell>
                      {isSuperAdmin && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {user.role !== "super_admin" && (
                              <>
                                <Button variant="ghost" size="sm" onClick={() => openRoleDialog(user)} title="Rolle ändern">
                                  <UserCog className="h-4 w-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => handleLockUser(user.id, !user.is_locked)}
                                  title={user.is_locked ? "Entsperren" : "Sperren"}
                                >
                                  {user.is_locked ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create User Dialog */}
        <Dialog open={createDialog} onOpenChange={setCreateDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>Neuen {getRoleInfo(createForm.role).label} erstellen</DialogTitle>
              <DialogDescription>
                Erstellen Sie einen neuen Benutzer mit der Rolle {getRoleInfo(createForm.role).label}.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Rolle</Label>
                <Select value={createForm.role} onValueChange={(v) => setCreateForm({ ...createForm, role: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dozent">Dozent</SelectItem>
                    <SelectItem value="admin">Administrator</SelectItem>
                    <SelectItem value="auditor">ISO-Auditor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  placeholder="Max Mustermann"
                />
              </div>
              <div className="space-y-2">
                <Label>E-Mail *</Label>
                <Input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  placeholder="email@beispiel.de"
                />
              </div>
              <div className="space-y-2">
                <Label>Passwort *</Label>
                <Input
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  placeholder="••••••••"
                />
              </div>
              <div className="space-y-2">
                <Label>Telefon</Label>
                <Input
                  value={createForm.phone}
                  onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                  placeholder="+49..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setCreateDialog(false)}>Abbrechen</Button>
              <Button onClick={handleCreateUser}>Erstellen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Change Role Dialog */}
        <Dialog open={roleDialog} onOpenChange={setRoleDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>Rolle ändern</DialogTitle>
              <DialogDescription>
                Ändern Sie die Rolle für {selectedUser?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Aktuelle Rolle</Label>
                <Badge className={getRoleInfo(selectedUser?.role).color}>
                  {getRoleInfo(selectedUser?.role).label}
                </Badge>
              </div>
              <div className="space-y-2">
                <Label>Neue Rolle</Label>
                <Select value={newRole} onValueChange={setNewRole}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="participant">Teilnehmer</SelectItem>
                    <SelectItem value="dozent">Dozent</SelectItem>
                    <SelectItem value="admin">Administrator</SelectItem>
                    <SelectItem value="auditor">ISO-Auditor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setRoleDialog(false)}>Abbrechen</Button>
              <Button onClick={handleChangeRole}>Speichern</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default UserManagement;
